import React, { useState, useEffect } from 'react';
import { Button, TextField, Box } from '@mui/material';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';

interface CommentBuilderProps {
  canEdit?: boolean;
  commentBuilderDefault?: string;
  showCommentBuilder: boolean;
  handleHideCommentBuilder: () => void;
  handleSaveCommentBuilder: (comment: string) => void;
  // Rendered above the text field, outside the editable value — for framing (e.g. "this
  // draft was AI-generated") that must never become part of the postable comment text.
  banner?: React.ReactNode;
  // The authoring voice differs by caller (assessor finding vs comment edit) - defaults preserve the assessor copy.
  dialogTitle?: string;
  fieldLabel?: string;
  saveLabel?: string;
}

const CommentBuilder: React.FC<CommentBuilderProps> = ({
  canEdit,
  commentBuilderDefault = '',
  handleHideCommentBuilder,
  showCommentBuilder,
  handleSaveCommentBuilder,
  banner,
  dialogTitle = 'Assessor Findings Comment Builder',
  fieldLabel = 'Assessor Finding Comment',
  saveLabel = 'Continue',
}) => {
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (showCommentBuilder) {
      setComment(commentBuilderDefault || '');
    }
  }, [showCommentBuilder, commentBuilderDefault]);

  const handleClose = () => {
    handleHideCommentBuilder();
    setComment('');
  };

  const handleSave = () => {
    handleSaveCommentBuilder(comment);
    handleHideCommentBuilder();
    setComment('');
  };

  return (
    <StandardDialog
      maxWidth="sm"
      handleClose={handleClose}
      isOpen={showCommentBuilder}
      title={dialogTitle}>
      {banner}
      <TextField
        id="assessor-finding-comment"
        autoFocus={showCommentBuilder}
        multiline
        rows={7}
        label={fieldLabel}
        fullWidth
        disabled={!canEdit}
        onChange={(e) => setComment(e.target.value)}
        value={comment}
        sx={{ mt: 2 }}
        inputProps={{ readOnly: false, sx: { overflowY: 'scroll' } }}
      />
      <StandardDialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined">
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              color="primary"
              variant="contained"
              type="submit"
              onClick={handleSave}>
              {saveLabel}
            </Button>
          )}
        </Box>
      </StandardDialogActions>
    </StandardDialog>
  );
};

export default CommentBuilder;